/**
 * Which of the product panel's two lists a connected product belongs in.
 *
 * Salesforce decides it, on the relation, via `Offer_In_Catalogue__c`
 * (ISSUE-3414 — Batu's field name, and his confirmation of the meaning on
 * 14 Sep):
 *
 *   true  — offer the product as its OWN catalogue entry: slideshow, staging,
 *           drone photos. Sold BESIDE the main product.
 *   false — not standalone: an option OF the main product. On today's data that
 *           is exactly `BLURRING` (90 relations) and `ARCHITECTSTAMP` (1).
 *
 * Dorian's prototype reaches the same two states from the design side and says
 * it more precisely than our ticket did — `panelExtras()` is "another catalogue
 * product offered beside this one, under an 'Extra services' heading on the
 * beige band", `bundleExtras()` is "an option of the product itself, so it goes
 * wherever the product goes: a plain untitled row… No title, because it is not a
 * set of something else — it is part of what was already chosen", and it names
 * extended blur and the architect's stamp as the only two. Hence
 * {@link CatalogueEntryRole}: beige-and-titled vs plain-and-untitled.
 *
 * THE DECISION IS THE RELATION'S, NOT THE KIND'S. Today the frontend infers the
 * layout from `RelationKind` — `ADD_ON` to the white option list, `EXTRA_SERVICE`
 * to the beige one — and that cuts across the real distinction: the 3D
 * floor-plan bands are `EXTRA_SERVICE` and belong with the standalone products,
 * while `ARCHITECTSTAMP` is `EXTRA_SERVICE` and belongs with the options. So the
 * flag has to be read on BOTH wire arrays, and the kind stops deciding layout.
 *
 * THE WIRE CONTRACT, agreed with the ISSUE-3415 session on 14 Sep: a
 * non-nullable enum `role` on every entry of BOTH `addons[]` and `extras[]`,
 * serialized as `"STANDALONE" | "MAIN_PRODUCT_OPTION"`, mapped from
 * `Offer_In_Catalogue__c` true/false. Never absent, never null — a legacy-fed
 * addon (a root CPR has no relations for, so `addons` falls back to legacy
 * `product.options`) is served `STANDALONE`, matching Salesforce's own default.
 *
 * `dependents[]` is covered structurally rather than by a second rule: the
 * backend stamps the flat product list before it nests dependents, and a
 * dependent is a plain product DTO, so its own `addons`/`extras` carry `role`
 * exactly like a top-level product's.
 *
 * NOT the same question as `requiresMainProductToWork`, which the backend is
 * adding to the addon DTO alongside this. `role` answers *which list*;
 * `requiresMainProductToWork` answers *can this be delivered without its parent
 * line*. They are independent — 584 of 723 active `ADD_ON` relations require
 * their main product AND are `STANDALONE`.
 *
 * Until it lands every entry resolves to `standalone`, which mirrors the
 * Salesforce field's own default of true. That is exactly ISSUE-3416's
 * no-regression case — "no regression when every flag is true" — so the panel
 * shows one titled list of everything, as it does today, and the option list
 * stays empty until the backend ships.
 */

/** Where an entry renders in the product panel. */
export type CatalogueEntryRole =
  /** Its own catalogue entry, sold beside the main product — beige, titled list. */
  | 'standalone'
  /** An option of the main product — plain, untitled rows. */
  | 'option'

/** The wire field this reads. The single binding point to ISSUE-3415. */
export const CATALOGUE_ROLE_FIELD = 'role' as const

/**
 * Which wire array an entry arrived in.
 *
 * The v2 catalogue carries NO relation kind on an addon or extra — that gap is
 * what ISSUE-3415 fills. Kept on the input because the entry's array is the one
 * thing the transform always knows, and a future rule may need it; the fallback
 * deliberately does NOT branch on it (see below).
 */
export type CatalogueEntrySource = 'addons' | 'extras'

/** Minimal shape — an addon or extra entry as the catalogue transform sees it. */
export interface CatalogueRoleInput {
  /**
   * The backend's classification. Typed loosely and optional ON PURPOSE: it is
   * non-nullable on the wire, but a frontend deployed ahead of ISSUE-3415 — or
   * pointed at an environment where it has not shipped — sees it absent.
   */
  role?: string | null
  /** The wire array it arrived in. Required: the fallback has nothing else. */
  source: CatalogueEntrySource
}

/** The backend's enum → our role. Unknown values fall through to the kind. */
const ROLE_BY_WIRE_VALUE: Readonly<Record<string, CatalogueEntryRole>> = {
  STANDALONE: 'standalone',
  MAIN_PRODUCT_OPTION: 'option',
}

export const roleFromWire = (value: string | null | undefined): CatalogueEntryRole | null =>
  (value ? ROLE_BY_WIRE_VALUE[value] : undefined) ?? null

/**
 * Resolve an entry's role.
 *
 * @param entry  The addon/extra entry, plus the array it arrived in.
 * @returns The backend's classification when present, else `standalone`.
 *
 * The fallback mirrors `Offer_In_Catalogue__c`'s own default of **true**, so an
 * environment whose backend has not shipped the field yet renders every entry
 * in the one titled list — the state ISSUE-3416 calls "every flag is true".
 * Branching on the source array instead was tried and was wrong: it put the
 * whole add-on list in the untitled option list and the heading disappeared,
 * which the live dev check caught immediately.
 */
export const catalogueEntryRole = (entry: CatalogueRoleInput): CatalogueEntryRole =>
  roleFromWire(entry.role) ?? 'standalone'
